import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Github, 
  Linkedin, 
  Twitter, 
  Mail, 
  Phone, 
  MapPin,
  Briefcase,
  GraduationCap,
  Building
} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-secondary-800 dark:bg-secondary-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-4"
          >
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-primary-800 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold">OSOP Placement</span>
            </div>
            <p className="text-secondary-300 text-sm leading-relaxed">
              Connecting talented students with innovative companies. 
              Your gateway to successful career opportunities.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-secondary-400 hover:text-primary-400 transition-colors duration-200">
                <Github className="w-5 h-5" />
              </a>
              <a href="#" className="text-secondary-400 hover:text-primary-400 transition-colors duration-200">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" className="text-secondary-400 hover:text-primary-400 transition-colors duration-200">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-4"
          >
            <h3 className="text-lg font-semibold">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-secondary-300 hover:text-primary-400 transition-colors duration-200 text-sm">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-secondary-300 hover:text-primary-400 transition-colors duration-200 text-sm">
                  Login
                </Link>
              </li>
              <li>
                <Link to="/register" className="text-secondary-300 hover:text-primary-400 transition-colors duration-200 text-sm">
                  Register
                </Link>
              </li>
              <li>
                <a href="#about" className="text-secondary-300 hover:text-primary-400 transition-colors duration-200 text-sm">
                  About Us
                </a>
              </li>
            </ul>
          </motion.div>

          {/* For Users */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-4"
          >
            <h3 className="text-lg font-semibold">For Users</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/student/dashboard" className="flex items-center space-x-2 text-secondary-300 hover:text-primary-400 transition-colors duration-200 text-sm">
                  <GraduationCap className="w-4 h-4" />
                  <span>Student Dashboard</span>
                </Link>
              </li>
              <li>
                <Link to="/company/dashboard" className="flex items-center space-x-2 text-secondary-300 hover:text-primary-400 transition-colors duration-200 text-sm">
                  <Building className="w-4 h-4" />
                  <span>Company Dashboard</span>
                </Link>
              </li>
              <li>
                <a href="#jobs" className="text-secondary-300 hover:text-primary-400 transition-colors duration-200 text-sm">
                  Browse Jobs
                </a>
              </li>
              <li>
                <a href="#companies" className="text-secondary-300 hover:text-primary-400 transition-colors duration-200 text-sm">
                  Featured Companies
                </a>
              </li>
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="space-y-4"
          >
            <h3 className="text-lg font-semibold">Contact Us</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-primary-400" />
                <span className="text-secondary-300 text-sm">info@osopplacement.com</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-4 h-4 text-primary-400" />
                <span className="text-secondary-300 text-sm">+1 (555) 123-4567</span>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-4 h-4 text-primary-400" />
                <span className="text-secondary-300 text-sm">123 Tech Street, Silicon Valley, CA</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="border-t border-secondary-700 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center"
        >
          <p className="text-secondary-400 text-sm">
            © {currentYear} OSOP Placement Portal. All rights reserved.
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <a href="#" className="text-secondary-400 hover:text-primary-400 transition-colors duration-200 text-sm">
              Privacy Policy
            </a>
            <a href="#" className="text-secondary-400 hover:text-primary-400 transition-colors duration-200 text-sm">
              Terms of Service
            </a>
            <a href="#" className="text-secondary-400 hover:text-primary-400 transition-colors duration-200 text-sm">
              Cookie Policy
            </a>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
